'use client'

import React, { useState, useMemo, useCallback } from 'react'
import type { CampAnmeldung, Teilnehmer, Camp } from '@/payload-types'
import AnmeldungenTable from './AnmeldungenTable.client'

type PopulatedAnmeldung = CampAnmeldung & {
  teilnehmer: Teilnehmer
  camp: Camp
  alternativeCamp?: Camp | null
}

interface FilteredAnmeldungenViewProps {
  anmeldungen: PopulatedAnmeldung[]
  alternativeRegistrations?: PopulatedAnmeldung[]
  groupedAnmeldungen: Record<string, { camp: Camp; anmeldungen: PopulatedAnmeldung[] }>
  selectedAnmeldungen: Set<string>
  onSelectAnmeldung: (anmeldungId: string) => void
  onSelectAll: () => void
  onBulkStatusUpdate?: () => void
  statusFilter?: string | null
  participantSearchTerm?: string
  currentCampId?: string
}

const FilteredAnmeldungenView = ({
  anmeldungen,
  alternativeRegistrations = [],
  groupedAnmeldungen,
  selectedAnmeldungen,
  onSelectAnmeldung,
  onSelectAll,
  onBulkStatusUpdate,
  statusFilter = null,
  participantSearchTerm = '',
  currentCampId,
}: FilteredAnmeldungenViewProps) => {
  const [activeTab, setActiveTab] = useState<'main' | 'alternative'>('main')

  // Create a callback that can be passed down to switch to main tab
  const switchToMainTab = useCallback(() => {
    setActiveTab('main')
  }, [])

  // Apply filters to alternative registrations (same logic as main)
  const filteredAlternativeRegistrations = useMemo(() => {
    let filtered = alternativeRegistrations

    // Filter by status
    if (statusFilter) {
      filtered = filtered.filter((anmeldung) => anmeldung.status === statusFilter)
    }

    // Filter by search term
    if (participantSearchTerm.trim()) {
      const searchLower = participantSearchTerm.toLowerCase()
      filtered = filtered.filter((anmeldung) => {
        const campTitle = (anmeldung.camp as Camp)?.title?.toLowerCase() || ''
        const campLocation = (anmeldung.camp as Camp)?.location?.toLowerCase() || ''
        const teilnehmerName =
          `${(anmeldung.teilnehmer as Teilnehmer)?.firstName || ''} ${(anmeldung.teilnehmer as Teilnehmer)?.lastName || ''}`.toLowerCase()
        const teilnehmerEmail = (anmeldung.teilnehmer as Teilnehmer)?.email?.toLowerCase() || ''

        return (
          campTitle.includes(searchLower) ||
          campLocation.includes(searchLower) ||
          teilnehmerName.includes(searchLower) ||
          teilnehmerEmail.includes(searchLower)
        )
      })
    }

    return filtered
  }, [alternativeRegistrations, statusFilter, participantSearchTerm])

  // Determine which dataset to show
  const activeRegistrations = activeTab === 'main' ? anmeldungen : filteredAlternativeRegistrations
  const hasAlternativeRegistrations = alternativeRegistrations.length > 0

  if (activeRegistrations.length === 0) {
    return (
      <div className="space-y-4">
        {/* Tabs */}
        {hasAlternativeRegistrations && (
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('main')}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'main'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Hauptanmeldungen ({anmeldungen.length})
              </button>
              <button
                onClick={() => setActiveTab('alternative')}
                className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'alternative'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Alternative Anmeldungen ({filteredAlternativeRegistrations.length})
              </button>
            </div>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
          <p className="text-gray-500">
            Keine Anmeldungen gefunden, die den aktuellen Filtern entsprechen.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Tabs */}
      {hasAlternativeRegistrations && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('main')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'main'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Hauptanmeldungen ({anmeldungen.length})
            </button>
            <button
              onClick={() => setActiveTab('alternative')}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'alternative'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Alternative Anmeldungen ({filteredAlternativeRegistrations.length})
            </button>
          </div>
        </div>
      )}

      <AnmeldungenTable
        anmeldungen={activeRegistrations}
        selectedAnmeldungen={selectedAnmeldungen}
        onSelectAnmeldung={onSelectAnmeldung}
        onSelectAll={onSelectAll}
        onBulkStatusUpdate={onBulkStatusUpdate || (() => {})}
        currentCampId={currentCampId}
        isAlternativeView={activeTab === 'alternative'}
        onSwitchToMainTab={switchToMainTab}
      />
    </div>
  )
}

export default FilteredAnmeldungenView
